import { Typography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TNotification } from "../model/store";
import { CaretDown, DotsThree } from "@phosphor-icons/react";
import { ListMagnifyingGlassIcon } from "@libs/zapi-ui/assets";
import { NotificationItem } from "./NotificationItem";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Button } from "@libs/zapi-ui/shadcn";

type TAggregatedNotificationItemProps = {
  notifications: TNotification[];
  projectId: number;
  projectName: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onMarkAsRead: (id: number) => void;
  onMarkAllProjectAsRead: (projectId: number) => void;
  onClose: () => void;
};

export function AggregatedNotificationItem({
  notifications,
  projectId,
  projectName,
  isExpanded,
  onToggleExpand,
  onMarkAsRead,
  onMarkAllProjectAsRead,
  onClose
}: TAggregatedNotificationItemProps) {
  const hasUnread = notifications.some((n) => !n.isRead);

  const handleHeaderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand();
  };

  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAllProjectAsRead(projectId);
  };

  const getContentText = () => {
    const notificationCount = notifications.length;
    const notificationWord = notificationCount === 1 ? "notification" : "notifications";

    return {
      title: projectName,
      subtitle: `Project "${projectName}" has ${notificationCount} ${notificationWord}`
    };
  };

  const content = getContentText();

  return (
    <div>
      <div
        className={cn(
          "p-3 transition-colors cursor-pointer hover:bg-innoveraZinc-50",
          hasUnread && "bg-innoveraZinc-50/50"
        )}
        onClick={handleHeaderClick}
      >
        <div className="flex gap-3">
          <div className="relative flex-shrink-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-innoveraZinc-200">
              <ListMagnifyingGlassIcon className="text-innoveraZinc-600" />
            </div>
            {hasUnread && <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-innoveraRed-400 rounded-full" />}
          </div>

          <div className="flex-1 min-w-0">
            <Typography
              variant="body1"
              className={cn("font-medium leading-tight mb-1", hasUnread && "text-innoveraZinc-900")}
            >
              {content.title}
            </Typography>
            <Typography variant="body2" className="text-innoveraZinc-600 leading-snug">
              {content.subtitle}
            </Typography>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <DotsThree size={16} weight="bold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleMarkAllAsRead} disabled={!hasUnread}>
                  Mark all as read
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div onClick={handleHeaderClick} className="cursor-pointer p-1">
              <CaretDown
                size={16}
                weight="bold"
                className={cn("text-innoveraZinc-400 transition-transform duration-200", isExpanded && "rotate-180")}
              />
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-innoveraZinc-100 bg-innoveraZinc-50/30">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={onMarkAsRead}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
}
